/**
 * Dialog displaying real-time keyword accuracy feedback during a SIM session.
 *
 * @module components/sim/takeSim/RealTimeFeedbackDialog
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from "react";
import {makeStyles} from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputLabel,
} from '@material-ui/core';
import {addAudioToClip, reset, updateStats} from '../../../redux/sims/actions';
import {simWizard} from './SimWizard';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import {cloneScript} from "../../../utils/scripts";

/**
 * RealTimeFeedbackDialog is a functional React component responsible for managing real-time
 * feedback interactions in a simulation or interactive learning environment. It provides
 * mechanisms for navigating through simulation clips, displaying results, and handling user actions.
 *
 * Props:
 * - `open` (boolean): Determines if the dialog is open or closed.
 * - `levelClips` (object): Contains information about the simulation clips at the current level.
 * - `activeClip` (number): Index of the currently active clip in the simulation.
 * - `loader` (ReactNode): Loader component to display while data is being processed.
 * - `continueSimLevel` (function): Function executed to proceed with the simulation level.
 * - `agentAudioPlayer` (object): Handles playback of agent-specific audio.
 * - `isPlaying` (boolean): Indicates whether audio is currently playing.
 * - `handleAutoPlayRecordStart` (function): Function to handle the initiation of auto-play and recording.
 * - `pauseRecordedAudio` (function): Pauses the playback of recorded audio.
 * - `playRecordedAudio` (function): Plays recorded audio.
 * - `setOpen` (function): Updates the state to open or close the feedback dialog.
 * - `intervalRef` (object): Ref object for managing intervals associated with clip progression.
 * - `setActiveClip` (function): Updates the index of the active simulation clip.
 * - `updateStats` (function): Updates the simulation progress statistics.
 * - `userResponse` (object): Contains user-specific preferences and settings, such as `mode`.
 * - `reset` (function): Resets the simulation to its initial state.
 * - `disableNextButton` (boolean): Disables the "next" button if set to `true`.
 * - `simId` (string): Unique identifier for the current simulation.
 * - `disableDoneButton` (boolean): Disables the "done" button if set to `true`.
 * - `level` (number): Specifies the current level in the simulation hierarchy.
 * - `addAudioToClip` (function): Adds audio to the simulation clip.
 * - `doneButtonHandler` (function): Executes actions upon clicking the "done" button.
 *
 * State:
 * - `isLoading` (boolean): Indicates whether data or resources are still being loaded.
 * - `isNextDisabled` (boolean): Tracks the state of the "next" button's enabled/disabled status.
 *
 * Effects:
 * - Handles updates to the "next" button's state when `disableNextButton` changes.
 * - Monitors and adjusts loading state based on the current clip's grading and natural language availability.
 *
 * Features:
 * - The dialog dynamically displays feedback on simulation clips, supporting features like grade scoring,
 *   missed keywords, test results, and natural language assessments.
 * - Provides handlers for navigating clips (`handleFeedbackNextChange`), closing the dialog (`handleClose`),
 *   and completing the simulation (`handleFeedbackDoneButton`).
 * - Supports modes such as `autoplay`, selectively initiating auto-play and recording based on conditions.
 *
 * Usage Context:
 * - Designed to be used within simulations or interactive modules to provide real-time user feedback.
 * - Centralizes logic for handling clip progression, feedback status, and user interaction workflows.
 *
 * Note:
 * - Assumes external styling is defined via Material-UI's `makeStyles`.
 * - Requires accurate `levelClips` structure, including `sim_scripts`, `grade_score`, `agent_recorded_audio`,
 *   and `natural_lenguage` properties, to function correctly.
 */
const RealTimeFeedbackDialog = ({
    open,
    levelClips,
    activeClip,
    loader,
    continueSimLevel,
    agentAudioPlayer,
    isPlaying,
    handleAutoPlayRecordStart,
    pauseRecordedAudio,
    playRecordedAudio,
    setOpen,
    intervalRef,
    setActiveClip,
    updateStats,
    userResponse,
    reset,
    disableNextButton,
    simId,
    disableDoneButton,
    level,
    addAudioToClip,
    doneButtonHandler
}) => {
    const useStyles = makeStyles((theme) => simWizard(theme));
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(false);
    const [isNextDisabled, setIsNextDisabled] = useState(false);

    useEffect(
        () => {
            setIsNextDisabled(disableNextButton);
        },
        [disableNextButton]
    );

    useEffect(() => {
        let loading = !(
            levelClips.sim_scripts[activeClip].grade_score ||
            (levelClips.sim_scripts[activeClip].grade_score === 0)
        );
        if (levelClips.sim_scripts[activeClip].natural_lenguage) {
            setIsLoading(loading);
        } else {
            setIsLoading(false);
        }

    }, [levelClips.sim_scripts, activeClip, loader]);

    /**
     * Handles changes for proceeding to the next feedback step in a simulation or interactive process.
     *
     * This function closes the feedback dialog, clears the interval for any ongoing process,
     * and resets the interval reference to `null`. Depending on the current state of the simulation
     * or interactive process, it either resets the simulation or advances to the next clip in
     * the sequence. Additionally, it updates the simulation statistics based on the current
     * progress and user settings.
     *
     * Behavior:
     * - If the current clip is the last one in the list, the simulation is reset, and stats are updated.
     * - If there are more clips remaining, the active clip is updated to the next clip, and simulation
     *   statistics are updated accordingly.
     * - For `autoplay` mode, the function starts the 'auto-play record' process for the next clip if
     *   specific conditions are met (e.g., the next clip is an agent clip without pre-recorded audio).
     *
     * State and Parameters:
     * - Modifies internal state variables including `activeClip`, `intervalRef`, and others.
     * - Makes use of external functions such as `setOpen`, `updateStats`, `reset`, and `setActiveClip`.
     *
     * Conditions Checked:
     * - Determines whether the current clip is the last in the sequence (`activeClip === length - 1`).
     * - Verifies conditions like `autoplay` mode, absence of pre-recorded audio, and clip type to decide
     *   whether to initiate the 'auto-play record' process.
     */
    const handleFeedbackNextChange = () => {
        setOpen(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        if (activeClip === levelClips.sim_scripts.length - 1) {
            updateStats(simId, level, 'started', userResponse.mode, activeClip, true, true);
            reset();
        } else {
            updateStats(simId, level, 'started', userResponse.mode, activeClip + 1, true, true);
            setActiveClip(activeClip + 1);
            if (
                userResponse.mode === 'autoplay'
                && !levelClips.sim_scripts[activeClip + 1].agent_recorded_audio
                && levelClips.sim_scripts[activeClip + 1].character.type === 'agent'
            ) {
                handleAutoPlayRecordStart(activeClip + 1, true);
            }
        }
    }

    /**
     * Handles the closing action for the Real Time Feedback dialog.
     * This function updates the simulation scripts by cloning the currently active script
     * and maintaining the existing state for other scripts. It modifies the associated
     * level clip, appends the necessary audio to it, clears any ongoing interval,
     * and updates the open state to `false`. If the user mode is set to 'autoplay',
     * it initiates the autoplay recording process for the active clip.
     *
     * Modifies the following states and references:
     * - Updates `levelClips` with the new simulation scripts.
     * - Clears `intervalRef` to ensure no conflicting intervals remain active.
     * - Sets the `setOpen` state to `false` to indicate closure.
     * - Triggers autoplay recording when in 'autoplay' mode.
     */
    const handleClose = (evt, reason) => {
        if (['backdropClick', 'escapeKeyDown'].includes(reason)) {
            return;
        }

        const newSimScripts = [];
        for (let i = 0; i < levelClips.sim_scripts.length; i += 1) {
            if (i === activeClip) {
                newSimScripts.push(cloneScript(levelClips.sim_scripts[activeClip]));
            } else {
                newSimScripts.push({...levelClips.sim_scripts[i]});
            }
        }

        const newLevelClip = {...levelClips, sim_scripts: newSimScripts};
        addAudioToClip(newLevelClip);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setOpen(false);
        if (userResponse.mode === 'autoplay') {
            handleAutoPlayRecordStart(activeClip, true);
        }
    }

    /**
     * Handles the "Done" button action for feedback completion in a simulation.
     *
     * This asynchronous function performs the following tasks sequentially:
     * - Closes the current feedback dialog by updating the state.
     * - Clears any active intervals associated with feedback.
     * - Executes the logic defined by the `doneButtonHandler` for the simulation and level.
     * - Updates simulation statistics, marking the level as completed along with relevant metadata.
     *
     * @param {string} simId - The identifier for the simulation.
     * @param {number} level - The level of the simulation being completed.
     */
    const handleFeedbackDoneButton = async (simId, level) => {
        setOpen(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        doneButtonHandler(simId, level);
        updateStats(simId, level, 'completed', userResponse.mode, activeClip, false);
    };

    return (
        <Dialog
            className={classes.modal}
            open={open}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="xs"
            scroll="paper"
        >
            <DialogTitle className={classes.label} id="form-dialog-title">
                Real Time Feedback <br/>
                {
                    (levelClips.sim_scripts[activeClip].natural_lenguage) ?
                        (
                            (!isLoading) ?
                                (
                                    (levelClips.sim_scripts[activeClip].grade_score >=
                                        (levelClips.sim_scripts[activeClip].overalConfidence ?
                                            levelClips.sim_scripts[activeClip].overalConfidence :
                                            60)) ?
                                        <CheckIcon className={classes.checkIcon}
                                                   style={{color: 'green', fontSize: '4rem'}}/> :
                                        <ClearIcon className={classes.crossIcon} color="error"
                                                   style={{fontSize: '4rem'}}/>
                                ) :
                                <div className={classes.loader}>
                                    <CircularProgress color="primary"/>
                                </div>
                        ) :
                        ''
                }
            </DialogTitle>
            <hr className={classes.hr}/>
            <DialogContent className={classes.modalContent}>
                <Grid container spacing={2}>
                    {
                        (
                            (levelClips.sim_scripts[activeClip].keywords &&
                                levelClips.sim_scripts[activeClip].keywords.length >
                                0 &&
                                !levelClips.sim_scripts[activeClip].natural_lenguage) ||
                            (levelClips.sim_scripts[activeClip].tests &&
                                levelClips.sim_scripts[activeClip].tests.length >
                                0 &&
                                levelClips.sim_scripts[activeClip].natural_lenguage)
                        ) ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: '100%'
                            }}>
                                {(!levelClips.sim_scripts[activeClip].natural_lenguage) ? (
                                    <Grid item xs={12} className={classes.grid}>
                                        <Grid item xs={6}>
                                            <InputLabel className={classes.missedKeywords}>
                                                Missed Keywords
                                            </InputLabel>
                                            {levelClips
                                                &&
                                                levelClips.sim_scripts[activeClip].missedKeywords
                                                &&
                                                levelClips.sim_scripts[activeClip].missedKeywords.map(
                                                    (missed) => (
                                                        <li className={classes.list}
                                                            key={missed.name}>
                                                            {missed.name}
                                                        </li>
                                                    ),
                                                )}
                                        </Grid>
                                        <Grid item xs={6} className={classes.labelGrid}>
                                            <InputLabel className={classes.correctKeywords}>
                                                Correct Keywords
                                            </InputLabel>
                                            {levelClips
                                                &&
                                                levelClips.sim_scripts[activeClip].correctKeywords
                                                &&
                                                levelClips.sim_scripts[activeClip].correctKeywords.map(
                                                    (correct) => {
                                                        return <li className={classes.list}
                                                                   key={correct.name}>
                                                            {correct.name}
                                                        </li>
                                                    },
                                                )}
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12} className={classes.grid} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        width: '100%'
                                    }}>
                                        <InputLabel style={{color: 'black', marginBottom: '5%'}}>
                                            Tests
                                        </InputLabel>
                                        <ul style={{width: '70%', margin: '0', padding: '0'}}>
                                            {levelClips
                                                &&
                                                levelClips.sim_scripts[activeClip].correctKeywords
                                                &&
                                                levelClips.sim_scripts[activeClip].correctKeywords.map(
                                                    (correct) => (
                                                        <li className={classes.list}
                                                            key={correct.name} style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            borderTop: '1px solid black',
                                                            paddingLeft: '5%',
                                                            paddingRight: '5%'
                                                        }}>
                                                            {levelClips.sim_scripts[activeClip].keywords.length >
                                                            0 ? correct.name : correct.name}
                                                            <CheckIcon className={classes.checkIcon}
                                                                       style={{color: 'green'}}/>
                                                        </li>
                                                    ),
                                                )}
                                            {levelClips
                                                &&
                                                levelClips.sim_scripts[activeClip].missedKeywords
                                                &&
                                                levelClips.sim_scripts[activeClip].missedKeywords.map(
                                                    (missed) => (
                                                        <li className={classes.list}
                                                            key={missed.name} style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            borderTop: '1px solid black',
                                                            paddingLeft: '5%',
                                                            paddingRight: '5%'
                                                        }}>
                                                            {levelClips.sim_scripts[activeClip].keywords.length >
                                                            0 ? missed.name : missed.name}
                                                            <ClearIcon className={classes.crossIcon}
                                                                       color="error"/>
                                                        </li>
                                                    ),
                                                )}
                                        </ul>
                                        {isLoading && (
                                            <div className={classes.loader}>
                                                <CircularProgress color="primary"/>
                                            </div>
                                        )}
                                    </Grid>
                                )}
                            </div>
                        ) : (
                            <Grid>
                                <h2>No {(!levelClips.sim_scripts[activeClip].natural_lenguage) ?
                                    'keywords' :
                                    'tests'} are present</h2>
                                <p>
                                    To try again, please use the Retry button. Otherwise,
                                    select the Next button to continue with assignment.
                                </p>
                            </Grid>
                        )}
                    {loader
                    && levelClips.sim_scripts[activeClip].keywords.length > 0 ? (
                        <div className={classes.loader}>
                            <CircularProgress color="primary"/>
                        </div>
                    ) : null}
                    <hr className={classes.hr}/>
                </Grid>
                {levelClips.sim_scripts[activeClip].character.type
                    === 'agent'
                    && (levelClips.sim_scripts[activeClip].audio_url
                        || (levelClips.sim_scripts[activeClip].agent_recorded_url
                            && continueSimLevel))
                    && !levelClips.sim_scripts[activeClip].agent_recorded_audio && (
                        <Grid container spacing={2}>
                            <div className={classes.player}>
                                <audio
                                    key={levelClips.sim_scripts[activeClip].agent_recorded_url ?
                                        levelClips.sim_scripts[activeClip].agent_recorded_url :
                                        levelClips.sim_scripts[activeClip].audio_url}
                                    ref={agentAudioPlayer}
                                >
                                    <source
                                        src={levelClips.sim_scripts[activeClip].agent_recorded_url ?
                                            levelClips.sim_scripts[activeClip].agent_recorded_url :
                                            levelClips.sim_scripts[activeClip].audio_url}
                                        type="audio/mp3"
                                    />
                                </audio>
                                {isPlaying ? (
                                    <IconButton
                                        color="primary"
                                        aria-label="pause"
                                        component="span"
                                        size="medium"
                                        onClick={pauseRecordedAudio}
                                    >
                                        <PauseCircleFilledIcon/>
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        className={classes.playerPlayBtn}
                                        aria-label="play"
                                        component="span"
                                        size="medium"
                                        onClick={playRecordedAudio}
                                    >
                                        <PlayCircleFilledIcon/>
                                    </IconButton>
                                )}
                                <span>Play recorded clip</span>
                            </div>
                            {
                                ((process.env.REACT_APP_STAGE === 'local') &&
                                    (!loader &&
                                        levelClips.sim_scripts[activeClip].transcription)) && (
                                    <Tooltip placement="top"
                                             title={levelClips.sim_scripts[activeClip].transcription}
                                             style={{textAlign: 'center', margin: '0 auto'}}
                                    >
                                        <div>
                                            <Typography variant="button" align="center">
                                                <strong>Transcript</strong>
                                            </Typography>
                                        </div>
                                    </Tooltip>
                                )}
                        </Grid>
                    )}
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="outlined" onClick={handleClose}>
                    Retry
                </Button>
                {activeClip === levelClips.sim_scripts.length - 1 ? (
                    <Button
                        onClick={() => handleFeedbackDoneButton(simId, level)}
                        color="primary"
                        variant="outlined"
                        disabled={disableDoneButton}
                    >
                        {disableDoneButton ? (
                            <CircularProgress color="primary" size={20}/>
                        ) : (
                            'Done'
                        )}
                    </Button>
                ) : (
                    <Button
                        disabled={isNextDisabled}
                        onClick={handleFeedbackNextChange}
                        color="primary"
                    >
                        Next
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

RealTimeFeedbackDialog.propTypes = {
    updateStats: PropTypes.func,
    reset: PropTypes.func,
    addAudioToClip: PropTypes.func,
    open: PropTypes.bool,
    activeClip: PropTypes.number,
    loader: PropTypes.bool,
    levelClips: PropTypes.object,
    continueSimLevel: PropTypes.bool,
    agentAudioPlayer: PropTypes.object,
    isPlaying: PropTypes.bool,
    handleAutoPlayRecordStart: PropTypes.func,
    pauseRecordedAudio: PropTypes.func,
    playRecordedAudio: PropTypes.func,
    setOpen: PropTypes.func,
    intervalRef: PropTypes.object,
    setActiveClip: PropTypes.func,
    userResponse: PropTypes.object,
    handleViewState: PropTypes.func,
    setStart: PropTypes.func,
    disableNextButton: PropTypes.bool,
    simId: PropTypes.string,
    stopRecording: PropTypes.func,
    recPer: PropTypes.bool,
    disableDoneButton: PropTypes.bool,
    level: PropTypes.string,
    doneButtonHandler: PropTypes.func,
};

const mapStateToProps = (state) => ({
    levelClips: state.sim.levelClips,
    userResponse: state.sim.userResponse,
    simId: state.sim.stats.result.sim_id,
});

const mapDispatchToProps = {
    updateStats,
    reset,
    addAudioToClip,
};

export default connect(mapStateToProps, mapDispatchToProps)(RealTimeFeedbackDialog);
